import type { Selector } from 'reselect';

import type { ModelConfig, RootState, SelectorCreator, Slicer } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { SingleOpportunity } from '@swf/generated/types/api/sports';

import effects from './effects';
import * as reducers from './reducers/betSlip';
import type { BetSlipState, PersistedBetSlipState } from './types/betSlip';

export const initialState: BetSlipState = {
  acceptedOdds: {},
  hasInvalidCombination: false,
  isLocked: false,
  isOpen: false,
  opportunities: {
    MULTIPLE: [],
    SINGLE: []
  },
  wallets: {},
  selections: [],
  submittedOpportunities: {
    SINGLE: [],
    MULTIPLE: []
  },
  receipt: {
    SINGLE: [],
    MULTIPLE: []
  },
  rehydrated: false
};

export const rehydratedInitialState: BetSlipState = {
  ...initialState,
  rehydrated: true
};

const selectors = (slice: Slicer<BetSlipState>, createSelector: SelectorCreator) => ({
  acceptedOdds() {
    return slice((state) => state.acceptedOdds);
  },
  isLocked() {
    return slice((state) => state.isLocked);
  },
  isOpen() {
    return slice((state) => state.isOpen);
  },
  opportunities() {
    return slice((state) => state.opportunities);
  },
  singleOpportunities() {
    return slice((state) => state.opportunities.SINGLE);
  },
  multipleOpportunities() {
    return slice((state) => state.opportunities.MULTIPLE);
  },
  receipt() {
    return slice((state) => state.receipt);
  },
  pendingSelectionCount(this: {
    selectionsCount: Selector<RootState, number>;
    singleOpportunities: Selector<RootState, SingleOpportunity[]>;
  }) {
    return createSelector(
      this.selectionsCount,
      this.singleOpportunities,
      (selectionsCount, singleOpportunities) =>
        Math.max(0, selectionsCount - singleOpportunities.length)
    );
  },
  pendingRemovalCount(this: {
    selectionsCount: Selector<RootState, number>;
    singleOpportunities: Selector<RootState, SingleOpportunity[]>;
  }) {
    return createSelector(
      this.selectionsCount,
      this.singleOpportunities,
      (selectionsCount, singleOpportunities) =>
        Math.max(0, singleOpportunities.length - selectionsCount)
    );
  },
  persistedState(): Selector<RootState, PersistedBetSlipState> {
    return slice(({ receipt, rehydrated, isLocked, ...state }) => state);
  },
  submittedOpportunities() {
    return slice((state) => state.submittedOpportunities);
  },
  selections() {
    return slice((state) => state.selections);
  },
  selectionsCount() {
    return slice((state) => state.selections.length);
  },
  wallets() {
    return slice((state) => state.wallets);
  },
  hasInvalidCombination() {
    return slice((state) => state.hasInvalidCombination);
  },
  errorMessage() {
    return slice((state) => state.errorMessage);
  },
  errorResponse() {
    return slice((state) => state.errorResponse);
  },
  isAutoDismissibleError() {
    return slice((state) => state.isAutoDismissibleError);
  },
  hasRehydrated() {
    return slice((state) => state.rehydrated);
  },
  requiredPlacementCurrency() {
    return slice((state) => state.requiredPlacementCurrency);
  },
  isCurrencyRestricted() {
    return createSelector(
      slice,
      (rootState) => select.account.getCurrency(rootState),
      ({ requiredPlacementCurrency }, selectedAccountCurrency) =>
        requiredPlacementCurrency && selectedAccountCurrency
          ? requiredPlacementCurrency !== selectedAccountCurrency
          : false
    );
  }
});

interface BetSlipModel extends ModelConfig<BetSlipState> {
  selectors: typeof selectors;
  reducers: typeof reducers;
  effects: typeof effects;
}

const betSlip: BetSlipModel = {
  state: { ...initialState },
  selectors,
  reducers,
  effects
};

export default betSlip;
